const _ = require("lodash");
const moment = require("moment");
const { _36To10 } = require("simple-base-converter");
const request = require("superagent");

const channelFields = require("../data/app__channelFields");
const { channels } = require("../data/app__channels");
const {
  LINKEDIN_PURCHASE_PRICE,
  MIN_BUDGET_FOUR_CHANNELS,
  CHANNEL_MULTIPLIER,
  CHANNELS_WITH_FIXED_BUDGET,
  CHANNEL_CPC
} = require("../common/constants/pricing");

const L1_TO_L4_MULTIPLIERS = require("../common/constants/jobCategoryL1ToL4Multipliers.js");

const CLOUDINARY_UPLOAD_PRESET = "oj05slrf";
const CLOUDINARY_UPLOAD_URL = "https://api.cloudinary.com/v1_1/dyiuxe5fa/image/upload";
const CLICK_PROMISE_FACTOR = 0.35;

const JOB_CATEGORIES_TO_NAMES = require("../data/jobCategories/name-mapping__de");
const NAMES_TO_JOB_CATEGORIES = _.invert(JOB_CATEGORIES_TO_NAMES);
const JOB_CATEGORIES_SUBCATEGORIES = require("../data/app__job-categories-sub-category-mapping.js");
const ALL_CHANNELS = channels.filter((c) => c.isActive);
const {
  validateDestinationLink
} = require("../../../controller/google-ads/services/validateDestinationLink.js");

// All hours regarding CM availability (Calculated to UTC: German time -2)
const MORNING_END_HOUR = parseInt(12);
const WORKING_HOUR_START = parseInt(5);
const WORKING_HOUR_END = parseInt(19);
const GOOGLE_ADS_ACCOUNT_ID = process.env.GOOGLE_ADS_ACCOUNT_ID;
const GOOGLE_ACCOUNT_ID_TO_OCID = JSON.parse(process.env.GOOGLE_ADS_ACCOUNT_ID_TO_OCID);

const REDDIT_ACCOUNT_ID = "fsfra8vmo";

const NORMAL_MIN_BUDGET = 500; // EUR
const NORMAL_MAX_BUDGET = 5000; // EUR
const NORMAL_DURATION = 30;
const MEDIA_BUDGET_MULTIPLIER = 0.5;
const FACEBOOK_CHANNELS = ["facebook", "instagram"];
const GOOGLE_CHANNELS = ["google-display", "ebay-kleinanzeigen"];

const validateEmail = function validateEmail(email) {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

const numberFormat = function numberFormat(x) {
  if (x === 0 || x === "0" || x === "NaN" || isNaN(x)) {
    return "0";
  }

  return (x || "")
    .toString()
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const DEFAULT_LOCALE = "de-DE";
const LANGUAGE_TO_LOCALE = {
  en: "en-US",
  de: "de-DE",
  es: "es-ES"
};

/**
 * Formats a number according to internationalization rules.
 *
 * @param {number} value - The number to format (defaults to 0)
 * @param {Object} options - Configuration options
 * @param {string} options.language - Language code (e.g., 'en', 'de', 'es'). Defaults to browser language or 'de'
 * @param {number} options.points - Number of decimal places to display (defaults to 0)
 * @param {string} options.style - Formatting style ('decimal', 'currency', 'percent', etc.) (defaults to 'decimal')
 * @returns {string} The formatted number as a string
 */
const formatIntlNumber = (
  value = 0,
  { language = navigator?.language?.split("-")[0] || "de", points = 0, style = "decimal" }
) => {
  let locale = DEFAULT_LOCALE;

  if (LANGUAGE_TO_LOCALE[language]) {
    locale = LANGUAGE_TO_LOCALE[language];
  } else {
    locale = navigator.language || DEFAULT_LOCALE;
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: points,
    maximumFractionDigits: points,
    style: style
  }).format(value);
};

const numberFormatTwoDecimals = (x) => {
  if (x === 0 || x === "0" || x === "NaN" || isNaN(x)) {
    return "0";
  }

  return (parseFloat(x).toFixed(2) || "")
    .toString()
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const slugify = function slugify(text) {
  return (text || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\-]+/g, "") // Remove all non-word chars
    .replace(/\-\-+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
};

function hasChannel(channelShortName, selectedChannels) {
  if (!channelShortName) {
    return false;
  }
  selectedChannels = selectedChannels || [];
  channelShortName = channelShortName.toLowerCase();
  if (selectedChannels && _.find(selectedChannels, { channel_short_name: channelShortName })) {
    return true;
  }
  return false;
}

/**
 * Returns the minimum clicks promised for a campaign
 * @param {number} lifeTimeBudget Budget of the campaign
 * @param {string} packageId The id of the package, or "newBooking" if it's not in a package
 * @param {object[]} selectedChannels The selected channels
 * @param {object[]} channels Array of all channels
 * @returns
 * @deprecated Use @see getMinimumClicks instead
 */
function renderMinimumClicks(lifeTimeBudget, packageId, selectedChannels = [], channels = []) {
  if (!selectedChannels?.length) {
    selectedChannels = ALL_CHANNELS.filter(({ channel_short_name }) =>
      channels.includes(channel_short_name)
    );
  }
  const budget = parseInt(lifeTimeBudget, 10);
  const packageIdLower = (packageId || "").toLowerCase();

  // For old campaigns, that didnt have a selectedChannel list we
  // calc a different minimum click amount
  if (!packageId && selectedChannels.length == 0) {
    return budget * CLICK_PROMISE_FACTOR;
  }

  if (packageIdLower.indexOf("twitter") !== -1) {
    return Math.abs(numberFormat(budget * 0.5));
  }

  if (packageIdLower.indexOf("indeed") !== -1) {
    return Math.abs(numberFormat(budget * 0.1));
  }

  if (packageIdLower.indexOf("google-search") !== -1) {
    return Math.abs(numberFormat(budget * 0.2));
  }

  if (packageIdLower.indexOf("linkedin") !== -1) {
    return Math.abs(numberFormat(budget * 0.1));
  }

  if (packageIdLower.indexOf("linkedin-feed") !== -1) {
    return Math.abs(numberFormat(budget * 0.1));
  }

  if (packageIdLower.indexOf("spotify") !== -1) {
    return Math.abs(numberFormat(budget * 0.1));
  }

  if (packageId && (selectedChannels.length == 0 || !selectedChannels)) {
    return budget * CLICK_PROMISE_FACTOR;
  }
  //---------------------------------------------------
  const channelLength = selectedChannels.length;

  let minimumClicks = parseInt(budget * CLICK_PROMISE_FACTOR, 10);
  const minimumClicksPerChannel = minimumClicks / channelLength;

  if (hasChannel("instagram", selectedChannels) && channelLength === 1) {
    const factor = 0.2;
    return (budget * factor).toFixed(0);
  }

  if (hasChannel("linkedin", selectedChannels) && channelLength === 1) {
    const factor = 0.05;
    return (budget * factor).toFixed(0);
  }

  if (hasChannel("spotify", selectedChannels) && channelLength === 1) {
    const factor = 0.05;
    return (budget * factor).toFixed(0);
  }

  if (hasChannel("google-search", selectedChannels) && channelLength === 1) {
    const factor = 0.05;
    return (budget * factor).toFixed(0);
  }

  if (hasChannel("indeed", selectedChannels) && channelLength === 1) {
    const factor = 0.05;
    return (budget * factor).toFixed(0);
  }

  if (hasChannel("google-search", selectedChannels)) {
    const factor = 0.3;

    minimumClicks -= minimumClicksPerChannel;
    minimumClicks += minimumClicksPerChannel * factor;
  }

  if (hasChannel("spotify", selectedChannels)) {
    const factor = 0.05;

    minimumClicks -= minimumClicksPerChannel;
    minimumClicks += minimumClicksPerChannel * factor;
  }

  if (hasChannel("indeed", selectedChannels)) {
    minimumClicks -= minimumClicksPerChannel;
    minimumClicks += minimumClicksPerChannel * 0.1;
  }

  if (hasChannel("pinterest", selectedChannels)) {
    minimumClicks -= minimumClicksPerChannel;
    minimumClicks += minimumClicksPerChannel * 0.4;
  }

  if (hasChannel("ebay-kleinanzeigen", selectedChannels)) {
    minimumClicks -= minimumClicksPerChannel;
    minimumClicks += minimumClicksPerChannel * 0.25;
  }

  if (hasChannel("twitter", selectedChannels)) {
    minimumClicks -= minimumClicksPerChannel;
    minimumClicks += minimumClicksPerChannel * 0.3;
  }

  if (hasChannel("linkedin", selectedChannels)) {
    minimumClicks -= minimumClicksPerChannel;
    const linkedInClicks = minimumClicksPerChannel * 0.1;
    minimumClicks += linkedInClicks;
  }

  if (hasChannel("linkedin-feed", selectedChannels)) {
    minimumClicks -= minimumClicksPerChannel;
    const linkedInClicks = minimumClicksPerChannel * 0.1;
    minimumClicks += linkedInClicks;
  }

  return minimumClicks.toFixed(0);
  // - indeed - twitter - linkedin - googleSearch;
}

/**
 * Returns a split of the budget for each channel
 * @returns Array of channels with their budget
 */
function getBudgetChannelSplit(lifetimeBudget, selectedChannels) {
  return selectedChannels
    .map(({ channel_short_name }) => {
      const budget = calculateChannelBudget(
        lifetimeBudget,
        selectedChannels,
        channel_short_name,
        false
      );
      return { name: channel_short_name, budget: parseFloat(budget) };
    })
    .sort((a, b) => b.budget - a.budget);
}

/**
 * Returns the minimum clicks promised for a campaign
 * @param {number} lifetimeBudget Total budget for the campaign
 * @param {object[]} selectedChannels Array of channels selected
 * @returns Total minimum clicks we promise to deliver
 */
function getMinimumClicks(lifetimeBudget, selectedChannels) {
  const budgetSplit = getBudgetChannelSplit(lifetimeBudget, selectedChannels);

  let minClicks = 0;

  budgetSplit.forEach(({ name, budget }) => {
    minClicks += Math.ceil(budget / CHANNEL_CPC[name]);
  });

  return minClicks;
}

function calculateAmountOfChannelsMerged(adsetChannels, channel) {
  switch (channel) {
    case "instagram":
    case "facebook":
      // Returns the amount of selected channels that can be merged
      return adsetChannels.filter((ch) => FACEBOOK_CHANNELS.includes(ch)).length;

    case "google-display":
    case "ebay-kleinanzeigen":
      // Returns the amount of selected channels that can be merged
      return adsetChannels.filter((ch) => GOOGLE_CHANNELS.includes(ch)).length;

    default:
      // For single channels: pinterest, spotigy, google-search, ...
      return 1;
  }
}

function getFixedBudgetsSum(channels) {
  const fixedBudgetsSum = CHANNELS_WITH_FIXED_BUDGET.reduce((acc, curr) => {
    if (channels.includes(curr.name)) {
      return acc + curr.budget;
    }

    return acc;
  }, 0);

  return fixedBudgetsSum;
}

function getBudgetToDivide(mediaBudget, channels) {
  const fixedBudgetsSum = getFixedBudgetsSum(channels);

  const budgetToDivide = parseInt(mediaBudget) - fixedBudgetsSum;

  return budgetToDivide;
}

function getChannelsWithoutFixedBudget(channels) {
  const channelsWithoutFixedBudget = channels.filter(
    (channel) => !CHANNELS_WITH_FIXED_BUDGET.some((fixChannel) => fixChannel.name === channel)
  );

  return channelsWithoutFixedBudget;
}

/**
 * Check if the campaign can be afforded with the default LINKEDIN_PURCHASE_PRICE
 * @param {number} adsetMediaBudget Media budget for the adset
 * @param {string[]} channels Names of the selected channels
 * @returns {boolean} True we can use LINKEDIN_PUCHASE_PRICE, false otherwise
 * @deprecated This is a temporary solution until we update the package pricing
 */
function canAffordLinkedinNormalPrice(adsetMediaBudget, channels) {
  if (!channels.includes("linkedin")) return true;
  if (adsetMediaBudget < LINKEDIN_PURCHASE_PRICE) return false;

  const channelsWithoutFixedBudget = getChannelsWithoutFixedBudget(channels);

  // Budget to divide with normal linkedin price
  const budgetToDivide = getBudgetToDivide(adsetMediaBudget, channels);
  const channelBudget = budgetToDivide / channelsWithoutFixedBudget.length;

  return channelBudget >= 30;
}

/**
 * Calculates the budget for a channel in an adset
 * @param {number} adsetMediaBudget Media budget for the adset
 * @param {string[]} adsetChannels Names of the selected channels
 * @param {string} channel Name of the channel to calculate the budget for
 * @param {boolean} mergeChannels True if we want to merge channels, false otherwise
 * @returns {string} Budget for the channel with two decimals
 */
function calculateAdsetChannelBudget(
  adsetMediaBudget,
  adsetChannels,
  channel,
  mergeChannels = true
) {
  const canAffordLinkedin = canAffordLinkedinNormalPrice(adsetMediaBudget, adsetChannels);

  if (!canAffordLinkedin) {
    const budget = channel === "linkedin" ? 90 : 30;
    return budget.toFixed(2);
  }

  if (channel === "linkedin") {
    return LINKEDIN_PURCHASE_PRICE.toFixed(2);
  }

  const adsetChannelsWithoutFixedBudget = getChannelsWithoutFixedBudget(adsetChannels);

  const budgetToDivide = getBudgetToDivide(adsetMediaBudget, adsetChannels);
  const channelBudget = budgetToDivide / adsetChannelsWithoutFixedBudget.length;

  if (!mergeChannels) {
    // Returns the single channel budget
    return parseFloat(channelBudget).toFixed(2);
  }

  // Calculates the amount of channels to merge
  const amountOfChannelsMerged = calculateAmountOfChannelsMerged(adsetChannels, channel);

  // Multiplies the channelBudget times the amount of channels merged
  return parseFloat(channelBudget * amountOfChannelsMerged).toFixed(2);
}

// Calculates the budget to set for a campaign
function calculateChannelBudget(lifetimeBudget, selectedChannels, channel, mergeChannels = true) {
  let channels = selectedChannels;
  if (selectedChannels?.[0] && typeof selectedChannels?.[0] == "object") {
    channels = _.map(_.uniqBy(selectedChannels, "channel_short_name"), "channel_short_name");
  }

  const mediaBudget = parseInt(lifetimeBudget, 10) * MEDIA_BUDGET_MULTIPLIER;
  const canAffordLinkedin = canAffordLinkedinNormalPrice(mediaBudget, selectedChannels);

  if (!canAffordLinkedin) {
    const budget = channel === "linkedin" ? 90 : 30;
    return budget.toFixed(2);
  }

  if (channel === "linkedin") {
    return LINKEDIN_PURCHASE_PRICE.toFixed(2);
  }
  const adsetChannelsWithoutFixedBudget = getChannelsWithoutFixedBudget(channels);

  const budgetToDivide = getBudgetToDivide(mediaBudget, channels);

  let channelBudget = budgetToDivide / adsetChannelsWithoutFixedBudget.length;
  // If the selected channel is facebook and we have insta as selected channel
  // then double the channel budget
  if (
    mergeChannels &&
    (channel == "facebook" || channel == "instagram") &&
    channels.includes("facebook") &&
    channels.includes("instagram")
  ) {
    channelBudget += channelBudget;
  } else if (
    mergeChannels &&
    (channel == "google-display" || channel == "youtube") &&
    channels.includes("google-display") &&
    channels.includes("youtube")
  ) {
    channelBudget += channelBudget;
    if (channels.includes("ebay-kleinanzeigen")) {
      channelBudget += channelBudget;
    }
  }
  channelBudget = parseFloat(channelBudget).toFixed(2);
  return channelBudget;
}

function getNrWorkingDaysBetweenDates(startDate, endDate) {
  let result = 0;

  const currentDate = new Date(startDate.getTime());
  while (currentDate <= endDate) {
    let weekDay = currentDate.getDay();
    if (weekDay != 0 && weekDay != 6) result++;

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return result;
}

function remainingDaysString(campaign, startDate) {
  const CAMPAIGN_DURATION_DAYS = getCampaignDuration(campaign);
  const date1 = new Date(startDate);
  date1.setDate(date1.getDate() + CAMPAIGN_DURATION_DAYS);
  const date2 = new Date();
  const timeDiff = Math.abs(date2.getTime() - date1.getTime());
  const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (date2.getTime() > date1.getTime()) {
    return "Geendet vor " + diffDays + " Tagen am " + moment(date1).format("DD.MM.YY");
  }

  return "noch " + diffDays + " Tage";
}

function remainingDaysStringInDays(campaign) {
  const CAMPAIGN_DURATION_DAYS = getCampaignDuration(campaign);
  return CAMPAIGN_DURATION_DAYS;
}

function getCampaignDuration(campaign) {
  let diffDays = parseInt(campaign.campaignDuration || 30);

  if (campaign && campaign.endDate) {
    const date1 = new Date(campaign.campaignLiveDate || campaign.createdDate);
    const date2 = new Date(campaign.endDate);
    const timeDiff = Math.abs(date2.getTime() - date1.getTime());
    diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  return diffDays;
}

function generateCampaignDateRange(
  { campaignStartDate, campaignEndDate, campaignDuration, campaignLiveDate },
  dateFormat = "YYYY-MM-DD"
) {
  let startDate;
  if (campaignStartDate) {
    startDate = moment(campaignStartDate);
  } else {
    startDate = campaignLiveDate ? moment(campaignLiveDate) : moment();
  }

  let endDate;
  if (campaignEndDate) {
    endDate = moment(campaignEndDate);
  } else {
    endDate = moment(startDate).add(parseInt(campaignDuration), "days");
  }

  return {
    startDate: startDate.format(dateFormat),
    endDate: endDate.format(dateFormat)
  };
}

function renderStatusSort(status) {
  const activeStatus = status === "ACTIVE";
  const draftStatus = status === "DRAFT";

  if (status === "PAUSED") {
    return 4;
  } else if (activeStatus) {
    return 6;
  } else if (status === "IN_PROGRESS") {
    return 2;
  } else if (status === "REJECTED") {
    return 30;
  } else if (status === "CANCELLED") {
    return 35;
  } else if (status === "STOPPED") {
    return 7;
  } else if (status === "FEEDBACK_NEEDED") {
    return 8;
  } else if (status === "SCHEDULED") {
    return 1;
  } else if (status === "WAITING_FOR_APPROVAL") {
    return 5;
  } else if (draftStatus) {
    return 1;
  } else {
    return 9;
  }
}
function getStatusInfos(status, t) {
  const activeStatus = status === "active" || status === "ACTIVE";

  if (activeStatus) {
    return t("The campaign is active and is being played out on social networks.");
  } else if (status === "IN_PROGRESS") {
    return t("We are currently reviewing the ad and preparing it for display.");
  } else if (status === "PAUSED") {
    return t("The ad is not currently displayed.");
  } else if (status === "FEEDBACK_NEEDED") {
    return t("To play the campaign you have to make changes to the campaign.");
  } else if (status === "FINISHED") {
    return t("The campaign is no longer active and has already been completely played out.");
  } else if (status === "REJECTED") {
    return t("The campaign was rejected.");
  } else if (status === "CANCELLED") {
    return t("The campaign has been cancelled.");
  } else if (status === "SCHEDULED") {
    return t("The campaign is planned.");
  }
  return "";
}

const listOfStatus = (t) => [
  {
    name: t("Draft"),
    value: "DRAFT",
    alternativeColor: "#C3FFE4",
    styles: { background: "#FF985C", color: "white" }
  },
  {
    name: t("In progress"),
    value: "IN_PROGRESS",
    alternativeColor: "#FFC82C",
    styles: { background: "#FF985C", color: "white" }
  },
  {
    name: t("Waiting for approval"),
    value: "WAITING_FOR_APPROVAL",
    alternativeColor: "#1E22FF",
    styles: { background: "#D5CCFF", color: "white" }
  },
  {
    name: t("Scheduled"),
    value: "SCHEDULED",
    alternativeColor: "#667EFE",
    styles: { background: "#667eff", color: "white" }
  },
  {
    name: t("Active"),
    value: "ACTIVE",
    alternativeColor: "#27DD8B",
    styles: { background: "#E1FAEF", color: "#22C380" }
  },
  {
    name: t("Paused"),
    value: "PAUSED",
    alternativeColor: "#DCEAFF",
    styles: { background: "#cde3e3", color: "white" }
  },
  {
    name: t("Finished"),
    value: "FINISHED",
    alternativeColor: "#1E1F46",
    styles: { background: "#5F5C70", color: "white" }
  },
  {
    name: t("Rejected"),
    value: "REJECTED",
    alternativeColor: "#FF5B3C",
    styles: { background: "#FFAEAE", color: "white" }
  },
  {
    name: t("Cancelled"),
    value: "CANCELLED",
    alternativeColor: "#FFBCAF",
    styles: { background: "#eebaff", color: "#ca2efe" }
  }
];

const renderStatus = function (status, t) {
  const activeStatus = status === "ACTIVE";
  const draftStatus = status === "DRAFT";

  if (status === "IN_PROGRESS") {
    return t ? t("In progress") : "In Bearbeitung";
  }
  if (activeStatus) {
    return t ? t("Active") : "Aktiv";
  }
  if (draftStatus) {
    return t ? t("Draft") : "Entwurf";
  }
  if (status === "PAUSED") {
    return t ? t("Paused") : "Pausiert";
  }
  if (status === "FEEDBACK_NEEDED") {
    return t ? t("Feedback needed") : "Eingaben nötig";
  }
  if (status === "COMMENT_ANSWER_NEEDED") {
    return t ? t("Comment answer needed") : "Anfrage wird bearbeitet";
  }
  if (status === "ACTION_REQUIRED") {
    return t ? t("Action required") : "Änderung nötig";
  }
  if (status === "REJECTED") {
    return t ? t("Rejected") : "Abgelehnt";
  }
  if (status === "CANCELLED") {
    return t ? t("Cancelled") : "Abgebrochen";
  }
  if (status === "FINISHED") {
    return t ? t("Finished") : "Beendet";
  }
  if (status === "PREVIEW") {
    return t ? t("Preview created") : "Vorschau erstellt";
  }
  if (status === "WAITING_FOR_APPROVAL") {
    return t ? t("Waiting for approval") : "Warten auf Freigabe";
  }

  if (status === "SCHEDULED") {
    return t ? t("Scheduled") : "Geplant";
  }

  return status;
};

const validateURL = function validateURL(textval) {
  const pattern = new RegExp(
    "^(https?:\\/\\/)?((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|((\\d{1,3}\\.){3}\\d{1,3}))(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*(\\?[;&a-z\\d%_.~+=-]*)?(\\#[-a-z\\d_]*)?$",
    "i"
  );
  const response = pattern.test(textval);
  return response;
};

const nFormatter = function nFormatter(num) {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(0).replace(/\.0$/, "") + "G";
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(0).replace(/\.0$/, "") + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0).replace(/\.0$/, "") + "K";
  }
  return num;
};

function sumValueFromDictKey(dict, keyName) {
  let total = 0;
  for (const key in dict) {
    // eslint-disable-next-line no-prototype-builtins
    if (dict.hasOwnProperty(key)) {
      const value = dict[key][keyName];
      total += parseInt(value, 10) || 0;
    }
  }
  return total;
}

function sumValueFromDictKeyFloat(dict, keyName) {
  let total = 0;
  for (const key in dict) {
    // eslint-disable-next-line no-prototype-builtins
    if (dict.hasOwnProperty(key)) {
      const value = dict[key][keyName];
      total += parseFloat(value) || 0;
    }
  }
  return total.toFixed(2);
}

function getSumByChannelAndMetricField(metric = {}, channelName, field) {
  let sum = _.toPairs(metric);

  sum = _.filter(sum, (channel) => {
    return channel[1].platform == channelName || channel[0] == channelName;
  });

  if (sum.length == 0) {
    return undefined;
  }

  sum = _.reduce(
    sum,
    (sum, item) => {
      let val = item[1][field];
      if (val == undefined || val == null || isNaN(val)) {
        val = 0;
      }
      return sum + parseInt(val, 10);
    },
    0
  );

  return sum;
}

function getSumByChannelAndMetricFieldFloat(metric = {}, channelName, field) {
  let sum = _.toPairs(metric);

  sum = _.filter(sum, (channel) => {
    return channel[1].platform == channelName;
  });

  if (sum.length == 0) {
    return undefined;
  }

  sum = _.reduce(
    sum,
    (sum, item) => {
      let val = item[1][field];

      if (val == undefined || val == null || isNaN(val) || `${val}`.trim() == "") {
        val = 0;
      }

      return sum + parseFloat(val);
    },
    0
  );

  return sum;
}

const parseMetricValuesForChannel = ({
  metrics,
  metricsDaily,
  creativesMetrics = [],
  channelNameShort,
  targetingData = [],
  channelId
}) => {
  const totalClicks = getSumByChannelAndMetricField(metrics, channelNameShort, "totalClicks");

  const totalSpent = getSumByChannelAndMetricFieldFloat(metrics, channelNameShort, "totalSpent");
  const averageCTR = getSumByChannelAndMetricFieldFloat(metrics, channelNameShort, "averageCTR");
  const averageFrequency = getSumByChannelAndMetricFieldFloat(
    metrics,
    channelNameShort,
    "averageFrequency"
  );
  const totalShares = getSumByChannelAndMetricFieldFloat(metrics, channelNameShort, "totalShares");
  const totalLikes = getSumByChannelAndMetricFieldFloat(metrics, channelNameShort, "totalLikes");
  const totalComments = getSumByChannelAndMetricFieldFloat(
    metrics,
    channelNameShort,
    "totalComments"
  );
  const totalImpressions = getSumByChannelAndMetricFieldFloat(
    metrics,
    channelNameShort,
    "totalImpressions"
  );

  const clicksToday = getSumByChannelAndMetricField(metricsDaily, channelNameShort, "totalClicks");

  //
  const channelMetrics = Object.values(_.pickBy(metrics, { platform: channelNameShort }));

  let targetingCount = targetingData?.length;
  if (channelNameShort == "facebook" || channelNameShort == "instagram") {
    targetingData = targetingData?.targeting?.flexible_spec?.[0] || {};
    targetingData = _.flatten(Object.values(targetingData));
    targetingCount = targetingData?.length || 0;
  }
  if (channelNameShort == "twitter") {
    targetingCount = _.min([0, targetingCount - 1]);
  }
  const lastUpdate = channelMetrics.length && channelMetrics[0] && channelMetrics[0].lastUpdate;
  const statuses = _.map(channelMetrics, "effectiveStatus");

  let effectiveStatus = "PAUSED";
  if (
    statuses.includes("ACTIVE") ||
    statuses.includes("AKTIV") ||
    channelNameShort === "linkedin"
  ) {
    effectiveStatus = "ACTIVE";
  } else if (statuses.includes("FINISHED") || statuses.includes("ABGESCHLOSSEN")) {
    effectiveStatus = "FINISHED";
  } else if (statuses.includes("PAUSED")) {
    effectiveStatus = "PAUSED";
  } else if (statuses.includes("INACTIVE") || statuses.includes("INAKTIV")) {
    effectiveStatus = "INACTIVE";
  }

  const mappedCreativesMetrics = creativesMetrics.map((creativeMetrics) => {
    const data = {
      ctr: parseFloat(creativeMetrics?.ctr || 0).toFixed(2),
      url: creativeMetrics?.asset?.url
    };

    return data;
  });

  return {
    creativeMetrics: mappedCreativesMetrics,
    totalClicks,
    totalSpent,
    clicksToday,
    channelId,
    channelStatus: effectiveStatus,
    lastUpdate,
    averageCTR,
    averageFrequency,
    totalShares,
    totalLikes,
    totalImpressions,
    totalComments,
    targetingCount,
    targetingData,
    startDate: ((channelMetrics && channelMetrics[0]) || {}).startDate,
    endDate: ((channelMetrics && channelMetrics[0]) || {}).endDate
  };
};

// Handle multiple files
function uploadFileToCloudStorage(input, storageName, callback) {
  const fileTypes = ["jpg", "jpeg", "png", "pdf", "txt"];

  const extension = input.files[0].name.split(".").pop().toLowerCase(), //file extension from input file
    isSuccess = fileTypes.indexOf(extension) > -1; //is extension in acceptable types

  if (!isSuccess) {
    fileSizeIsValid = false;
    callback(true, "Das Dokument teile ist nicht richtig");
    return;
  }

  // Check file size, cloudinary has a limit of 10MB = 10000000 bytes

  let fileSizeIsValid = true;
  if (input.files && input.files[0] && input.files[0].size && input.files[0].size > 10000000) {
    fileSizeIsValid = false;
    callback(true, "Das Dokument ist zu groß");
    return;
  }

  if (input.files && input.files[0]) {
    const reader = new FileReader();
    if (fileSizeIsValid) {
      reader.onload = () => {
        //Upload to cloudinary
        let upload = request
          .post(CLOUDINARY_UPLOAD_URL)
          .field("upload_preset", CLOUDINARY_UPLOAD_PRESET)
          .field("file", input.files[0])
          .field("folder", storageName);

        upload.end((err, response) => {
          if (err) {
            // eslint-disable-next-line no-console
            console.log("error:uploadFileToCloudStorage", "called", {
              err,
              storageName
            });
            return callback(true, err);
          }

          if (response.body.secure_url !== "") {
            const body = response.body;

            const data = {
              name: body.public_id,
              image_url: body.secure_url,
              extension: body.format,
              height: body.height,
              width: body.width,
              type: "upload",
              etag: body.etag || "",
              phash: body.phash || ""
            };

            callback(null, [data]);
          }
        });
      };
    }

    reader.readAsDataURL(input.files[0]);
  } else {
    callback(true, "No files chosen");
  }
}

function renderTwitterName(name) {
  const dataRef = encodeURIComponent(
    JSON.stringify({
      account: [
        { key: "objectives", operands: [] },
        { key: "campaignName", operator: "1", operands: [name] }
      ]
    })
  );

  let link = `https://ads.x.com/ads_manager/18ce54tajo3/campaigns/`;
  link += `?endDate=${moment().format("YYYY-MM-DD")}`;
  link += `&filterSet=1`;
  link += `&startDate=${moment().subtract(7, "d").format("YYYY-MM-DD")}`;
  link += `&filters=${dataRef}`;

  return link;
}

function renderTwitterLink(twtiterAPIId = "") {
  if (!twtiterAPIId) {
    return "";
  }
  const twitterUIId = _36To10(twtiterAPIId);
  const endDateFormat = moment().format("YYYY-MM-DD");
  const link = `https://ads.x.com/reporting_dashboard/18ce54tajo3/campaigns?endDate=${endDateFormat}&includeDeleted=true&columnSet=0&filterSet=1&filters=`;
  const filters = encodeURIComponent(
    `[{"key":"AdvertiserHadDeliveryFilter","operator":"None","operands":["false"]},{"key":"AdvertiserCampaignIdFilter","operator":"None","operands":["${twitterUIId}"]}]`
  );
  return link + filters;
}

const serialize = function (obj = new Map()) {
  obj = Array.from(obj).reduce((obj, [key, value]) => {
    obj[key] = value;
    return obj;
  }, {});
  obj = JSON.stringify(obj);
  return encodeURIComponent(obj);
};

function sameDay(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function uriToJSON(urijson) {
  return JSON.parse(decodeURIComponent(urijson));
}

function getL1JobCategory(jobCategoryId = "") {
  if (!jobCategoryId) {
    return "";
  }
  let jobCategoryIdL1 = jobCategoryId.split("_")[0];
  jobCategoryIdL1 += "_L2000_L3000";

  return jobCategoryIdL1;
}

function getL2JobCategory(jobCategoryId = "") {
  if (!jobCategoryId) {
    return "";
  }
  let jobCategoryIdL2 = jobCategoryId.split("_").slice(0, 2);
  jobCategoryIdL2 = jobCategoryIdL2.join("_");
  jobCategoryIdL2 += "_L3000";
  return jobCategoryIdL2;
}

function getL3JobCategory(jobCategoryId = "") {
  if (!jobCategoryId) {
    return "";
  }
  let jobCategoryIdL3 = jobCategoryId.split("_").slice(0, 3);
  jobCategoryIdL3 = jobCategoryIdL3.join("_");
  return jobCategoryIdL3;
}

function getJobCategoryIdLevel(jobCategoryId) {
  if (!jobCategoryId) return 0;
  const levels = jobCategoryId.split("_");
  return levels.length;
}

function getL1JobCategories() {
  return Object.keys(JOB_CATEGORIES_SUBCATEGORIES).filter((categoryLabel) =>
    categoryLabel.includes("_L2000_L3000")
  );
}

function getJobCategoryName(jobCategoryId) {
  return JOB_CATEGORIES_TO_NAMES[jobCategoryId] || "";
}

function getJobCategoryId(jobCategoryName) {
  return NAMES_TO_JOB_CATEGORIES[jobCategoryName] || "";
}

function getJobSubcategories(parentCategoryId = "") {
  return JOB_CATEGORIES_SUBCATEGORIES[parentCategoryId] || [];
}

function getCompanyImageFolderName({ orgaId, companyId, orgaName, companyName, metadata }) {
  if (companyId) {
    return prepareCompanyImageFolderName(orgaId, companyId, metadata);
  }

  return prepareImageFolderName(orgaName, companyName, metadata);
}

function prepareImageFolderName(orgaName = "", companyName = "", metadata = {}) {
  companyName = companyName.trim();
  companyName = companyName.replace(/ /g, "_");
  companyName = companyName.replace(/\./g, "_");
  companyName = companyName.replace(/\&/g, "_");
  companyName = companyName.replace(/\-/g, "_");
  companyName = companyName.replace(/–/g, "_"); // Replaces en dashes with underscores
  companyName = companyName.toLocaleLowerCase();
  companyName = companyName.replace(/ä/g, "ae");
  companyName = companyName.replace(/ö/g, "oe");
  companyName = companyName.replace(/ü/g, "ue");
  companyName = companyName.replace(/ß/g, "ss");
  companyName = companyName.replace(/%/g, "percent");
  companyName = companyName.replace(/\//g, "_"); // Replaces slashes with underscores

  orgaName = orgaName.trim();
  orgaName = orgaName.replace(/ /g, "_");
  orgaName = orgaName.replace(/\./g, "_");
  orgaName = orgaName.replace(/\&/g, "_");
  orgaName = orgaName.replace(/\-/g, "_");
  orgaName = orgaName.replace(/–/g, "_"); // Replaces en dashes with underscores
  orgaName = orgaName.toLocaleLowerCase();
  orgaName = orgaName.replace(/ä/g, "ae");
  orgaName = orgaName.replace(/ö/g, "oe");
  orgaName = orgaName.replace(/ü/g, "ue");
  orgaName = orgaName.replace(/ß/g, "ss");
  orgaName = orgaName.replace(/%/g, "percent");
  orgaName = orgaName.replace(/\//g, "_"); // Replaces slashes with underscores

  let folderName = `tb-campaign-photos/${orgaName || "none"}/${companyName}`;

  if (metadata.jobTitle) {
    let cleanTitle = metadata.jobTitle;
    cleanTitle = cleanTitle.trim();
    cleanTitle = cleanTitle.replace(/ /g, "_");
    cleanTitle = cleanTitle.replace(/\./g, "_");
    cleanTitle = cleanTitle.replace(/\&/g, "_");
    cleanTitle = cleanTitle.replace(/\-/g, "_");
    cleanTitle = cleanTitle.replace(/–/g, "_");
    cleanTitle = cleanTitle.toLocaleLowerCase();
    cleanTitle = cleanTitle.replace(/ä/g, "ae");
    cleanTitle = cleanTitle.replace(/ö/g, "oe");
    cleanTitle = cleanTitle.replace(/ü/g, "ue");
    cleanTitle = cleanTitle.replace(/ß/g, "ss");
    cleanTitle = cleanTitle.replace(/%/g, "percent");
    cleanTitle = cleanTitle.replace(/\//g, "_"); // Replaces slashes with underscores
    metadata.jobTitle = cleanTitle;
  }
  return { folderName, companyName, orgaName, metadata };
}

function prepareCompanyImageFolderName(orgaId, companyId, metadata = {}) {
  let folderName = `tb-campaign-photos/${orgaId}/${companyId}`;

  if (metadata.jobTitle) {
    let cleanTitle = metadata.jobTitle;
    cleanTitle = cleanTitle.trim();
    cleanTitle = cleanTitle.replace(/ /g, "_");
    cleanTitle = cleanTitle.replace(/\./g, "_");
    cleanTitle = cleanTitle.replace(/\&/g, "_");
    cleanTitle = cleanTitle.replace(/\-/g, "_");
    cleanTitle = cleanTitle.replace(/–/g, "_");
    cleanTitle = cleanTitle.toLocaleLowerCase();
    cleanTitle = cleanTitle.replace(/ä/g, "ae");
    cleanTitle = cleanTitle.replace(/ö/g, "oe");
    cleanTitle = cleanTitle.replace(/ü/g, "ue");
    cleanTitle = cleanTitle.replace(/ß/g, "ss");
    cleanTitle = cleanTitle.replace(/%/g, "percent");
    cleanTitle = cleanTitle.replace(/\//g, "_"); // Replaces slashes with underscores
    metadata.jobTitle = cleanTitle;
  }

  return { folderName, companyId, orgaId, metadata };
}

function renderRedditUrlParams(redditCampaignId) {
  const date = new Date();

  const dateStart = new Date();
  dateStart.setDate(date.getDate() - 30);

  return encodeURIComponent(
    JSON.stringify({
      // "startDate": `${yearStart}-${monthStart}-${dayStart}`,
      // "endDate":`${year}-${month}-${day}`,
      selectedAdgroups: [],
      selectedAds: [],
      selectedCampaigns: [parseInt(redditCampaignId, 10)]
    })
  );
}
function getPlatformUrl({
  channel,
  campaignId,
  campaignName,
  tbCampaignId,
  fbAccountId = "1775523439373366",
  googleAdsAccountId = GOOGLE_ADS_ACCOUNT_ID,
  twitterAdsCampaignId
}) {
  if (fbAccountId && fbAccountId.includes("act_")) {
    fbAccountId = fbAccountId.replace("act_", "");
  }
  const linkGoogle = `https://ads.google.com/aw/ads?ocid=${GOOGLE_ACCOUNT_ID_TO_OCID[googleAdsAccountId]}&campaignId=${campaignId}`;
  const baseUrlMeta = "https://business.facebook.com/adsmanager/manage/campaigns";
  if (channel === "facebook") {
    return `${baseUrlMeta}?act=${fbAccountId}&business_id=1775520372707006&selected_campaign_ids=${campaignId}&filter_set=CAMPAIGN_GROUP_DELIVERY_STATUS-STRING_SET\x1EIN\x1E["active","deleted","error","inactive","off","pending"]\x1DSEARCH_BY_CAMPAIGN_GROUP_IDS-STRING_SET\x1EANY\x1E["${campaignId}"]`;
  }
  if (channel === "instagram") {
    return `${baseUrlMeta}?act=${fbAccountId}&business_id=1775520372707006&selected_campaign_ids=${campaignId}&filter_set=CAMPAIGN_GROUP_DELIVERY_STATUS-STRING_SET\x1EIN\x1E["active","deleted","error","inactive","off","pending"]\x1DSEARCH_BY_CAMPAIGN_GROUP_IDS-STRING_SET\x1EANY\x1E["${campaignId}"]&impression_breakdown=publisher_platform`;
  }
  if (channel === "google-display") {
    return linkGoogle;
  }
  if (channel === "youtube") {
    return linkGoogle;
  }
  if (channel === "ebay-kleinanzeigen") {
    return linkGoogle;
  }
  if (channel === "google-search") {
    return `https://ads.google.com/aw/keywords?campaignId=${campaignId}&ocid=${GOOGLE_ACCOUNT_ID_TO_OCID[googleAdsAccountId]}`;
  }

  if (channel === "twitter") {
    return renderTwitterLink(twitterAdsCampaignId || campaignId);
  }
  if (channel === "tiktok") {
    return (
      "https://ads.tiktok.com/i18n/perf/campaign?aadvid=6919858416093495298&columns=campaign_budget,ad_id,budget,bid,schedule,ad_name,click_cnt,creative_id,stat_cost,cpc,cpm,show_cnt,ctr,time_attr_convert_cnt,time_attr_conversion_cost,time_attr_conversion_rate,time_attr_effect_cnt,time_attr_effect_cost,time_attr_effect_rate&keyword=" +
      campaignId
    );
  }
  if (channel === "pinterest") {
    return `https://ads.pinterest.com/advertiser/549761223319/reporting/adgroups/?campaignIds=[${campaignId}]`;
  }
  if (channel === "reddit") {
    const name = tbCampaignId;
    let url = `/dashboard?end=2023-01-16&entity=campaigns`;
    url += `&fields=` + encodeURIComponent(`impressions,ecpm,clicks,cpc,ctr,spend`);
    url +=
      `&filters=` +
      encodeURIComponent(`[{"field":"campaignId.name","operator":"LIKE","value":"${name}"}]`);
    url += `&fromTab=true&graphMetric=spend&pageSize=100&params=1555917483132974674`;
    url += `&source=tab&start=2023-01-10&view=Default&viewSetup`;

    return `https://ads.reddit.com/account/${REDDIT_ACCOUNT_ID}` + url;
  }
  if (channel === "snapchat") {
    return `https://ads.snapchat.com/7d358a77-afd2-4f43-b6b2-8e24fb18e322/campaigns/${campaignId}/?endDate=2020-08-01T22%3A00%3A00.000Z&granularity=TOTAL&startDate=2020-05-15T22%3A00%3A00.000Z`;
  }

  if (channel === "linkedin-feed") {
    return `https://www.linkedin.com/campaignmanager/accounts/${campaignId}/campaign-groups`;
  }

  if (channel === "indeed") {
    return `https://employers.indeed.com/reporting/ads/${campaignId}`;
  }
  if (channel === "linkedin") {
    let searchTerm = (campaignName || "").toLocaleLowerCase().replace("m/w/d", "");
    searchTerm = searchTerm.replace("m/w/i", "");
    searchTerm = searchTerm.replace("m/w/x", "");
    searchTerm = searchTerm.replace("w/m/d", "");
    searchTerm = searchTerm.replace(/\(/g, "");
    searchTerm = searchTerm.replace(/\)/g, "");
    searchTerm = searchTerm.replace(/-/g, " ");
    searchTerm = searchTerm.replace(/:/g, " ");

    return `https://www.linkedin.com/talent/jobs/promoted-basic-jobs/promoted-jobs?searchKeyword=${encodeURIComponent(
      searchTerm
    )}`;
  }
  if (channel === "spotify") {
    return `https://adstudio.spotify.com/account/071cf05d-7f27-4ab4-9581-d2638624f7f4/dashboard/campaigns?campaign-limit=20&campaign-offset=0&campaign-searchWord=${campaignId}&flight-limit=20&flight-offset=0`;
  }
}

function getLeadFormLink({
  jobCategoryId,
  campaignId,
  baseUrlTalentBait = "https://app.talentbait.de",
  channel = "",
  subdomain = "app",
  sendFrontMessage,
  sendSlackMessage
}) {
  const l1JobCategoryId = getL1JobCategory(jobCategoryId);
  const l1JobCategoryName = slugify(getJobCategoryName(l1JobCategoryId));
  if (baseUrlTalentBait == "https://app.talentbait.de") {
    baseUrlTalentBait = `https://${subdomain}.talentbait.de`;
  }

  let leadFormlink = `${baseUrlTalentBait}/jetzt-bewerben/${l1JobCategoryName}/${campaignId}`;
  if (!jobCategoryId) {
    leadFormlink = `${baseUrlTalentBait}/jetzt-bewerben/${campaignId}`;
  }
  if (channel) {
    leadFormlink = leadFormlink + `?utm_source=${channel}`;
  }

  validateDestinationLink({
    url: leadFormlink,
    sendFrontMessage,
    sendSlackMessage
  });

  return leadFormlink;
}

const EMOJIS =
  /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;

function handleEmojis(text) {
  return EMOJIS.test(text);
}

function removeEmojis(text = "") {
  return text.replace(EMOJIS, "");
}

function handleSpecialChar(text) {
  return /[\!\?\:]/.test(text);
}

function isCampaignManagerAvailable(cm, day, timeOfDay, withWorkingHours = true) {
  if (!cm) {
    return false;
  }
  const now = moment().utc();
  const hour = now.hour();
  if (!day || !timeOfDay) {
    day = moment().format("dddd").toLowerCase();
    timeOfDay = hour < MORNING_END_HOUR ? "morning" : "afternoon";
  }
  if (["saturday", "sunday"].includes(day)) {
    return false;
  }
  if (withWorkingHours && (hour <= WORKING_HOUR_START || hour >= WORKING_HOUR_END)) {
    return false;
  }
  if (!cm.isActive) {
    return false;
  }
  if (cm.availability && cm.availability[day] && cm.availability[day][timeOfDay]) {
    return true;
  }
  return false;
}

// Gets a list of CM that would be available
function getNextAvailableCampaignManagers(campaignManagers = []) {
  let availableCMs = undefined;
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday"];
  const times = ["morning", "afternoon"];
  const now = moment().utc();
  const hour = now.hour();
  const today = moment().format("dddd").toLowerCase();
  // TODO: Check the edge case for when getting the next time slot at night (should be tomorrow morning)
  const currentTimeOfDay = hour < MORNING_END_HOUR ? "morning" : "afternoon";
  const todayIndex = days.indexOf(today);
  const currentTimeOfDayIndex = times.indexOf(currentTimeOfDay);

  for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
    const dayIndexToCheck = (todayIndex + dayIndex) % days.length;
    for (let timeIndex = 0; timeIndex < times.length; timeIndex++) {
      const timeIndexToCheck = (currentTimeOfDayIndex + timeIndex) % times.length;
      const day = days[dayIndexToCheck];
      const timeOfDay = times[timeIndexToCheck];
      const cms = _.filter(campaignManagers, (cm) =>
        isCampaignManagerAvailable(cm, day, timeOfDay, false)
      );
      if (cms && cms.length > 0) {
        availableCMs = cms;
        break;
      }
    }
    if (availableCMs && availableCMs.length > 0) {
      break;
    }
  }
  return availableCMs;
}

function getCMwithMostComments(campaignManagers, comments) {
  let campaignManagersCount = _.map(campaignManagers, (campaignManager) => ({
    ...campaignManager,
    commentCount: 0
  }));
  _.forEach(comments, (comment) => {
    const { userName = "" } = comment;
    const cmIndex = _.findIndex(campaignManagersCount, { userName });
    if (userName && cmIndex > -1) {
      campaignManagersCount[cmIndex].commentCount++;
    }
  });
  const maxCommentCount = _.maxBy(campaignManagersCount, "commentCount")?.commentCount;
  const cmsWithMostComments = _.filter(campaignManagersCount, { commentCount: maxCommentCount });
  const selectedCM = _.sample(cmsWithMostComments) || campaignManagersCount[0];
  return selectedCM;
}

function getFrontLink(csmConversationUrl) {
  let conversationId = csmConversationUrl.split("/");
  conversationId = conversationId[conversationId.length - 1];
  return `https://app.frontapp.com/open/${conversationId}`;
}

function getFieldName(channelName) {
  let fieldName = _.find(channelFields, {
    channel: channelName
  });
  fieldName = fieldName && fieldName.name;
  return fieldName;
}

function cleanTitle(jobTitle = "") {
  let cleanTitle = jobTitle;

  const abbreviations = ["m/w/d", "w/m/d", "f/m/d", "m/f/d", "w/m/i", "f/m/i", "m/f/i", "m/w/i"];

  for (let abbreviation of abbreviations) {
    cleanTitle = cleanTitle.replace(abbreviation, "");
    cleanTitle = cleanTitle.replace(abbreviation.toUpperCase(), "");
    cleanTitle = cleanTitle.replace("()", "");
  }

  cleanTitle = removeStopWords(cleanTitle);

  cleanTitle = cleanTitle.trim();

  // Ensure the title ends with a letter
  while (!cleanTitle.match(/\p{L}$/u) && cleanTitle.length > 0) {
    cleanTitle = cleanTitle.slice(0, -1);
  }

  return cleanTitle;
}

function cleanTitleForKeywordsSearch(jobTitle = "") {
  let cleanTitleTransformted = cleanTitle(jobTitle);
  cleanTitleTransformted = cleanTitleTransformted
    .replaceAll("/", " ")
    .replaceAll(".", " ")
    .replaceAll("-", " ");
  return cleanTitleTransformted;
}

function removeStopWords(text = "") {
  const stopWords = [
    "der",
    "die",
    "das",
    "ein",
    "eine",
    "einer",
    "eines",
    "einem",
    "einen",
    "und",
    "oder",
    "aber",
    "weil",
    "wenn",
    "wie",
    "wo",
    "was",
    "wer",
    "wird",
    "also",
    "sich",
    "nicht",
    "haben",
    "sein",
    "aus",
    "bei",
    "mit",
    "von",
    "nach",
    "vor",
    "zu",
    "über",
    "am",
    "im",
    "an",
    "auf",
    "aus",
    "zu",
    "um",
    "in",
    "für",
    "bis"
  ];

  // Split the text into words
  let words = text.split(" ");

  // Filter out the stop words
  words = words.filter((word) => !stopWords.includes(word.toLowerCase()));

  // Join the remaining words back together into a string
  text = words.join(" ");

  return text;
}

function calculateExtraLocationBudget(locations) {
  const extraBudgetLocations = locations.slice(2).map((elem) => {
    if (elem.type === "state") {
      return { label: elem.label, price: 400 };
    } else {
      return { label: elem.label, price: 200 };
    }
  });
  return extraBudgetLocations;
}

function getTextForAcceptingPreviews(
  jobTitle,
  companyName,
  selectedChannels,
  orgaId,
  campaignId,
  userName,
  orgaName,
  url
) {
  const confLink = url || (window && window.baseUrlInteralTools);

  let text = `✅✅✅ Kampagne wurde von *${userName}* (${orgaName}) freigegeben \n`;
  text += `ℹ️ Title: ${jobTitle}\n`;
  text += `🏢 Firma: ${companyName}\n`;
  text += `📱 Kanäle: ${selectedChannels.map(
    (item) => item.channel_full_name || item.channel_short_name
  )} \n`;
  text += `${url || confLink}/admin-campaign/${orgaId}/${campaignId}`;
  return text;
}

function replaceAdPreviewApplyLink(adPreviewData, newCampaignId) {
  const { released = {}, staging = {} } = adPreviewData;
  _.values(released).forEach((channelPreview) => {
    if (channelPreview && channelPreview.jobPostingUrl) {
      channelPreview.jobPostingUrl = channelPreview.jobPostingUrl.replace(
        /\/jetzt-bewerben\/\w+/,
        `/jetzt-bewerben/${newCampaignId}`
      );
    }
  });
  _.values(staging).forEach((channelPreview) => {
    if (channelPreview && channelPreview.jobPostingUrl) {
      channelPreview.jobPostingUrl = channelPreview.jobPostingUrl.replace(
        /\/jetzt-bewerben\/\w+/,
        `/jetzt-bewerben/${newCampaignId}`
      );
    }
  });
  return adPreviewData;
}

function isUrlVideo(mediaUrl = "") {
  return mediaUrl.includes(".mp4");
}

function stockUrlToStockId({ url }) {
  const idMatch = /[^-]*?(.jpg)/g.exec(url);
  return idMatch && idMatch[0] ? idMatch[0].substring(0, idMatch[0].length - 4) : "";
}

function isImageUrlUnlicensedStock(url) {
  if (
    url &&
    ((url.includes(".shutterstock.com/") && // is a shutterstock photo
      !url.startsWith("https://download.shutterstock.com/")) || // not a licensed link
      url.startsWith("https://ak.picdn.net/shutterstock/")) // shutterstock has a variant of the stock photos where the url domain is not .shutterstock.com
  ) {
    return true;
  }
  return false;
}

function optimizeCloudinaryImage(url, cropStyle = "fit") {
  if (!url.includes("res.cloudinary")) {
    return url;
  }
  let originalImage = url.split("/image/upload/");

  let optimizedImage =
    originalImage &&
    originalImage[0] + `/image/upload/w_auto,f_auto,q_auto,c_${cropStyle}/` + originalImage[1];
  return optimizedImage;
}

function getMinClicksByDuration(minClicks, duration, oldDuration) {
  const numericDuration = parseInt(duration);
  const newMinClicks = Math.ceil((minClicks / oldDuration) * numericDuration);
  return newMinClicks;
}

function getNewThresholds(duration, oldThresholds, oldDuration) {
  const numericDuration = parseInt(duration);
  const newBudgetThresholds = { ...oldThresholds };
  for (let level of Object.keys(newBudgetThresholds)) {
    newBudgetThresholds[level] = Math.max(
      Math.ceil((newBudgetThresholds[level] / oldDuration) * numericDuration),
      200
    );
  }
  return newBudgetThresholds;
}

/**
 * Returns the minimum allowed budget for the given duration
 * Doesn't go below the normal minimum budget
 * @param {number} duration Campaign duration
 * @returns {number} The minimum budget for the given duration
 */
function getMinBudget(duration) {
  const numericDuration = parseInt(duration);

  return Math.max(
    Math.floor((NORMAL_MIN_BUDGET / NORMAL_DURATION) * numericDuration),
    NORMAL_MIN_BUDGET
  );
}

/**
 * Returns the maximum allowed budget for the given duration
 * Doesn't go below the normal maximum budget
 * @param {number} duration Campaign duration
 * @returns {number} The maximum budget for the given duration
 */
function getMaxBudget(duration) {
  const numericDuration = parseInt(duration);
  const expectedMax = Math.ceil((NORMAL_MAX_BUDGET / NORMAL_DURATION) * numericDuration);

  return Math.max(expectedMax, NORMAL_MAX_BUDGET);
}

function getBudgetByDuration(budget, duration, previousDuration = NORMAL_DURATION) {
  const numericDuration = parseInt(duration);
  const recommendedBudget = Math.ceil((budget / previousDuration) * numericDuration);
  const maxBudget = getMaxBudget(duration);
  const minBudget = getMinBudget(duration);
  if (recommendedBudget > maxBudget) return maxBudget;
  if (recommendedBudget < minBudget) return minBudget;
  return recommendedBudget;
}

/**
 * Returns the multiplier for the given L1 category
 * @param {string} jobCategoryId Job category ID
 * @returns {number} The multiplier
 */
function getCategoryMultiplier(jobCategoryId) {
  return L1_TO_L4_MULTIPLIERS[jobCategoryId] || 1;
  // const l1CategoryId = getL1JobCategory(jobCategoryId);

  // return L1_MULTIPLIERS[l1CategoryId] || 1;
}

// Sorts all channels that we have based on their ranking on how likely they should be recommended
// for the given job categoryId
function sortChannelsBasedOnJobCategoryRanking(
  jobCategoryId,
  jobCategoryIdToChannelRankings,
  order = "desc"
) {
  if (!jobCategoryIdToChannelRankings) {
    return ALL_CHANNELS;
  }

  const level1JobCategory = getL1JobCategory(jobCategoryId);
  const channelRankings =
    _.find(jobCategoryIdToChannelRankings, {
      id: level1JobCategory
    })?.ranking || {};
  const allChannelsWithRankings = ALL_CHANNELS.map((channelData) => ({
    ...channelData,
    ranking: channelRankings?.[channelData.channel_short_name] || 0
  }));
  return _.orderBy(allChannelsWithRankings, "ranking", order);
}

// Given a budget and a list of channels, return a list of recommended channels.
function updateChannelsWithBudget({ budget, channels, campaignDuration = 30 }) {
  const selectedChannels = [];

  const allChannels = [...channels];

  // If duplicate channels were added, then make sure to remove them
  let uniqueChannels = _.uniqBy(allChannels);
  // Filter out channels who require longer campaign duration than the currently selected one
  uniqueChannels = _.filter(
    uniqueChannels,
    ({ minCampaignDuration }) => campaignDuration >= minCampaignDuration
  );
  var totalBudgetInPackage = 0;
  // TODO: Take campaign duration into consideration when summing price
  // Add channels to the recommended channels as long as the budget is
  // still suffiecent.
  for (let i = 0; i < uniqueChannels.length; i++) {
    // At this point the budget is to big
    const channelPrice = parseFloat(uniqueChannels[i].price);
    if (channelPrice + totalBudgetInPackage > budget) {
      break;
    }

    // There's a minimum budget to add the 4th channel
    if (i === 3 && budget < MIN_BUDGET_FOUR_CHANNELS) break;

    // Sum up the min price of the current channel to the total
    totalBudgetInPackage += channelPrice;
    selectedChannels.push(uniqueChannels[i]);
  }

  return selectedChannels.slice(0, 4);
}

/**
 * Calculates the different budget thresholds, applying the channel multiplier
 * @param {object} predictions Predictions received from the ML server
 * @param {number} predictions.budget_500 Level 1 budget
 * @param {number} predictions.budget_1000 Level 2 budget
 * @param {number} predictions.budget_1500 Level 3 budget
 * @param {string} predictions.jobCategoryId The job category ID
 * @param {number} channelMultiplier The channel multiplier, defaults to the ENV variable
 * @returns The different budget levels
 */
function calculateBudgetLevels(
  { budget_500, budget_1000, budget_1500, jobCategoryId },
  channelMultiplier = CHANNEL_MULTIPLIER
) {
  const categoryMultiplier = getCategoryMultiplier(jobCategoryId);
  const multiplier = categoryMultiplier * channelMultiplier;

  return {
    budget500: Math.ceil(budget_500 * multiplier),
    budget1000: Math.ceil(budget_1000 * multiplier),
    budget1500: Math.ceil(budget_1500 * multiplier)
  };
}
/**
 * Forces a URL to use the HTTPS protocol.
 *
 * @param {string} url - The input URL, which may use http or another protocol.
 * @returns {string} The URL converted to use https. If the URL is invalid, returns the original input.
 *
 * @example
 * forceHttps('http://example.com'); // 'https://example.com'
 */
function forceHttps(url) {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.protocol = "https:";
    return parsedUrl.toString();
  } catch (error) {
    console.warn("Invalid or empty URL:", url);
    return url; // or you could throw an error instead
  }
}

module.exports = {
  stockUrlToStockId,
  isImageUrlUnlicensedStock,
  handleEmojis,
  removeEmojis,
  handleSpecialChar,
  getPlatformUrl,
  getLeadFormLink,
  renderRedditUrlParams,
  serialize,
  uriToJSON,
  sameDay,
  renderTwitterName,
  renderTwitterLink,
  sumValueFromDictKey,
  sumValueFromDictKeyFloat,
  getStatusInfos,
  getNrWorkingDaysBetweenDates,
  getSumByChannelAndMetricField,
  getSumByChannelAndMetricFieldFloat,
  parseMetricValuesForChannel,
  renderMinimumClicks,
  getMinimumClicks,
  calculateChannelBudget,
  calculateAdsetChannelBudget,
  listOfStatus,
  getCampaignDuration,
  remainingDaysString,
  remainingDaysStringInDays,
  generateCampaignDateRange,
  validateEmail,
  numberFormat,
  formatIntlNumber,
  numberFormatTwoDecimals,
  slugify,
  renderStatusSort,
  renderStatus,
  getJobCategoryIdLevel,
  validateURL,
  nFormatter,
  hasChannel,
  uploadFileToCloudStorage,
  prepareImageFolderName,
  prepareCompanyImageFolderName,
  getCompanyImageFolderName,
  getFrontLink,
  getFieldName,
  cleanTitle,
  cleanTitleForKeywordsSearch,
  removeStopWords,
  getTextForAcceptingPreviews,
  calculateExtraLocationBudget,
  forceHttps,
  // Campaign Managers
  isCampaignManagerAvailable,
  getNextAvailableCampaignManagers,
  getCMwithMostComments,

  // Ad Preview
  replaceAdPreviewApplyLink,
  isUrlVideo,
  optimizeCloudinaryImage,

  // Job Category helper
  getL1JobCategory,
  getL2JobCategory,
  getL3JobCategory,
  getL1JobCategories,
  getJobSubcategories,
  getJobCategoryName,
  getJobCategoryId,

  // Budget calculator
  getCategoryMultiplier,
  calculateBudgetLevels,
  getMinBudget,
  getMaxBudget,
  getNewThresholds,
  getBudgetByDuration,
  getMinClicksByDuration,
  sortChannelsBasedOnJobCategoryRanking,
  updateChannelsWithBudget
};
